/**
 * @jsxFrag
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Suspense, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, loadQuery, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import type { DocNode } from '@atlaskit/adf-schema';
import { cssMap, jsx } from '@atlaskit/css';
import { type MediaProvider, ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EmojiProvider } from '@atlaskit/emoji/resource';
import { MediaClientProvider } from '@atlaskit/media-client-react';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import type { RendererAppearance } from '@atlaskit/renderer';
import Spinner from '@atlaskit/spinner';
import { ContextualAnalyticsData, FireScreenAnalytics, SCREEN } from '@atlassian/analytics-bridge';
import { CommonHubExtensionsDependenciesProvider } from '@atlassian/common-hub-extensions-dependencies-provider';
import {
	GenericErrorPage,
	getMediaProvider,
	helpCenterTypeToProduct,
	type HubAppProps,
	HubBootstrapWrapper,
	HubContext,
	HubErrorBoundary,
	type NavigationStyleContextType,
	NavigationStyleContext,
	useHubContext,
} from '@atlassian/company-hub-common';
import { mapApiToNavigationOptions } from '@atlassian/company-hub-common/navigation-bar-branding';
import {
	AiWidgetProvider,
	AiWidgetProviderQuery,
	type aiWidgetProviderQuery,
} from '@atlassian/csm-ai-search-trigger';
import { HubAppShell } from '@atlassian/hub-app-shell';
import { getEmojiProvider } from '@atlassian/hub-providers';
import { HubRenderer } from '@atlassian/hub-renderer';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useProductExtensionHandlers } from '../../controllers/use-product-extension-handlers';
import {
	hubBootstrapResource,
	hubPageResource,
	rovoWidgetResource,
} from '../../services/resources';
import { useRelayResource } from '../../services/resources/utils';
import { AiCornerAgentRenderer } from '../../ui/ai-corner-agent-renderer';
import { HubsDocumentTitle } from '../../ui/document-title';

import type { hubQuery } from './__generated__/hubQuery.graphql';
import messages from './messages';

declare global {
	interface Window {
		__SSR_RENDERED__?: boolean;
	}
}

const styles = cssMap({
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
	},
});

interface HubInnerProps {
	appearance?: RendererAppearance;
	extensionHandlers: any;
	helpCenterData: any;
	mediaProvider: MediaProvider;
	parsedAdf: DocNode;
	providerFactory: ProviderFactory;
}

const HubInner = ({
	appearance = 'full-page',
	parsedAdf,
	mediaProvider,
	providerFactory,
	extensionHandlers,
	helpCenterData,
}: HubInnerProps) => {
	return (
		<MediaClientProvider clientConfig={mediaProvider.viewMediaClientConfig}>
			<HubRenderer
				document={parsedAdf}
				appearance={appearance}
				dataProviders={providerFactory}
				product={helpCenterTypeToProduct(helpCenterData?.type || '')}
				additionalExtensionHandlers={extensionHandlers}
				contentId={helpCenterData?.pages?.[0]?.id || ''}
				PropsProvider={CommonHubExtensionsDependenciesProvider}
			/>
		</MediaClientProvider>
	);
};

const Hub = ({
	queryReference,
	appearance = 'full-page',
	showPageNameAsHubDocumentTitle = false,
}: {
	appearance?: RendererAppearance;
	queryReference: PreloadedQuery<hubQuery>;
	showPageNameAsHubDocumentTitle?: boolean;
}) => {
	// When the fix flag is ON, `HubAppShell` is rendered one level up in
	// `HubApp` so it stays mounted across page navigations. When the flag is
	// OFF, `Hub` renders `HubAppShell` itself (original behaviour).
	di(HubAppShell, useRelayResource);
	const hubContext = useHubContext();
	if (!hubContext) {
		throw new Error('HubContext not found. HubBootstrap must be used as a parent component.');
	}

	const {
		user,
		cloudId,
		env: { helpCenterAri },
	} = hubContext;

	// When the fix flag is ON, `pageMediaConfig` is fetched here (page-scoped)
	// so the bootstrap query stays page-agnostic and is not refetched on every
	// page navigation. When the flag is OFF, `pageMediaConfig` is skipped here
	// and remains on the bootstrap query as before.
	/* eslint-disable @atlassian/relay/unused-fields */
	const data = usePreloadedQuery<hubQuery>(
		graphql`
			query hubQuery(
				$cloudId: ID!
				$hubAri: ID!
				$pageAri: ID!
				$operationType: HelpCenterMediaConfigOperationType
			) {
				helpCenter(cloudId: $cloudId) {
					pageMediaConfig: hubMediaConfig(ari: $pageAri, operationType: $operationType)
						@required(action: THROW) {
						asapIssuer @required(action: THROW)
						mediaCollectionName @required(action: THROW)
						mediaToken @required(action: THROW)
						mediaUrl @required(action: THROW)
					}
					helpCenterById(helpCenterAri: $hubAri) @optIn(to: "HelpCenterAggBeta") {
						__typename
						... on HelpCenter {
							type @optIn(to: "HelpCenterTypeTest")
							name {
								default
							}

							helpCenterBranding @optIn(to: "HelpCenterBrandingTest") {
								navigationBar @optIn(to: "HelpCenterNavigationBarTest") {
									backgroundColor
									borderColor
									borderType
									cornerRadius
									padding
									positionType
									textColor
								}
							}

							pages(filter: { ids: [$pageAri] }) {
								id
								name {
									default
								}
								pageLayout {
									layoutAdf {
										content {
											edges {
												node {
													content
												}
											}
										}
									}
								}
							}
						}
						... on QueryError {
							message
						}
					}
				}
			}
		`,
		queryReference,
	);
	/* eslint-enable @atlassian/relay/unused-fields */

	// When the fix flag is ON, read `pageMediaConfig` from the page-scoped
	// hubQuery result. When OFF, it comes from the bootstrap via `hubContext`.
	const pageMediaConfigFromQuery = data.helpCenter?.pageMediaConfig;
	const pageMediaConfigFromContext = hubContext.pageMediaConfig;

	const pageMediaConfig = fg('hubs_fix_rerendering_bootstrap_page_media')
		? pageMediaConfigFromQuery
		: pageMediaConfigFromContext;

	if (!pageMediaConfig) {
		throw new Error('pageMediaConfig not found');
	}
	const { mediaCollectionName, asapIssuer, mediaToken, mediaUrl } = pageMediaConfig;

	const helpCenterData = data.helpCenter?.helpCenterById;

	if (helpCenterData?.__typename === 'QueryError') {
		throw new Error(`QueryError in hub query: ${helpCenterData.message}`);
	}

	if (helpCenterData?.__typename !== 'HelpCenter') {
		throw new Error('HelpCenter not found in hub query');
	}

	const helpCenterType = helpCenterData?.type || '';

	const title =
		showPageNameAsHubDocumentTitle && helpCenterData.pages?.[0]?.name?.default
			? helpCenterData.pages?.[0]?.name?.default
			: '';

	const mediaProvider: MediaProvider = getMediaProvider(
		mediaCollectionName,
		asapIssuer,
		mediaToken,
		mediaUrl,
	);
	const emojiProvider: Promise<EmojiProvider> = getEmojiProvider({
		cloudId: queryReference.variables.cloudId,
		// NOTE: id is not yet available in the user object, but will be in the near future
		// as per https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/186871/overview#comment-659373408.
		// userId is optional for getEmojiProvider so just leaving this here for now.
		userId: user.id,
	});

	const providerFactory = React.useMemo(() => {
		return ProviderFactory.create({
			mediaProvider: Promise.resolve(mediaProvider),
			emojiProvider: Promise.resolve(emojiProvider),
		});
	}, [mediaProvider, emojiProvider]);

	const layoutAdf =
		helpCenterData.pages?.[0]?.pageLayout?.layoutAdf?.content?.edges
			?.map((edge) => edge?.node?.content)
			?.join('') || '{}';

	const parsedAdf = JSON.parse(layoutAdf) as DocNode;

	const environment = useRelayEnvironmentProvider().getEnvironment({});

	// The Rovo widget query is preloaded as a route resource (in parallel with the
	// hub bootstrap/page queries) so the help-hub callout banner's "Ask Rovo" search
	// box doesn't wait on a late, lazily-fired query. The resource is only attached to
	// the route when the `hub_preload_rovo_widget_query` gate is on (see the route
	// factories); when it isn't attached, the router never loads it and this reads
	// `null`. The reference is forwarded to the (help-hub-only) callout banner handler.
	const { queryReference: rovoWidgetProviderQueryReference } = useRelayResource(rovoWidgetResource);

	// Use product extension handlers hook to get additional handlers for the product
	const extensionHandlers = useProductExtensionHandlers({
		contentId: queryReference.variables.hubAri,
		PropsProvider: CommonHubExtensionsDependenciesProvider,
		product: helpCenterType,
		rovoWidgetQueryReference: rovoWidgetProviderQueryReference,
	});

	const aiWidgetProviderQueryReference = useMemo(() => {
		if (helpCenterType !== 'CUSTOMER_SERVICE') {
			return null;
		}

		return loadQuery(environment, AiWidgetProviderQuery, {
			cloudId,
			helpCenterAri,
		}) as PreloadedQuery<aiWidgetProviderQuery>;
	}, [helpCenterType, environment, cloudId, helpCenterAri]);

	const navigationBar = helpCenterData?.helpCenterBranding?.navigationBar ?? null;

	const { branding } = useHubContext();
	const brandColor = branding?.colors?.primary;

	const navStyleOptions = useMemo<NavigationStyleContextType>(
		() => ({
			options: navigationBar ? mapApiToNavigationOptions(navigationBar, brandColor) : null,
			setOptions: () => void 0,
		}),
		[navigationBar, brandColor],
	);

	// When the fix flag is ON, re-provide `HubContext` with `pageMediaConfig`
	// from the page-scoped query so descendants can read it via
	// `useHubContext()` without a separate hook. When the flag is OFF the
	// bootstrap already set `pageMediaConfig` on the context, so no
	// re-provision is needed.
	const hubContextWithPageMedia = useMemo(
		() => ({ ...hubContext, pageMediaConfig }),
		[hubContext, pageMediaConfig],
	);

	const inner = (
		<NavigationStyleContext.Provider value={navStyleOptions}>
			<HubsDocumentTitle title={title} shouldRenderDocumentTitle={showPageNameAsHubDocumentTitle}>
				<>
					<HubInner
						appearance={appearance}
						parsedAdf={parsedAdf}
						mediaProvider={mediaProvider}
						providerFactory={providerFactory}
						extensionHandlers={extensionHandlers}
						helpCenterData={helpCenterData}
					/>
					<Suspense fallback={<UFOLoadHold name="ai-corner-agent-renderer-loader" />}>
						{aiWidgetProviderQueryReference && (
							<ErrorBoundary fallback={null}>
								<AiWidgetProvider queryReference={aiWidgetProviderQueryReference}>
									<AiCornerAgentRenderer />
								</AiWidgetProvider>
							</ErrorBoundary>
						)}
					</Suspense>
				</>
			</HubsDocumentTitle>
		</NavigationStyleContext.Provider>
	);

	// When the fix flag is ON, wrap with a HubContext.Provider that merges
	// in the page-scoped `pageMediaConfig`. When OFF, restore the original
	// behaviour: render `HubAppShell` here (wrapping `inner`) and use the
	// bootstrap context which already has `pageMediaConfig`.
	return fg('hubs_fix_rerendering_bootstrap_page_media') ? (
		<HubContext.Provider value={hubContextWithPageMedia}>{inner}</HubContext.Provider>
	) : (
		<HubAppShell>{inner}</HubAppShell>
	);
};

export const HubApp = (props: HubAppProps): JSX.Element | null => {
	di(useRelayResource);
	di(HubBootstrapWrapper, HubAppShell);

	const { appearance = 'full-page' } = props;
	const { queryReference: hubPageQueryReference } = useRelayResource(hubPageResource);

	const { queryReference: hubBootstrapQueryReference } = useRelayResource(hubBootstrapResource);

	const { formatMessage } = useIntl();

	if (!hubPageQueryReference || !hubBootstrapQueryReference) {
		return null;
	}

	const cloudId = hubBootstrapQueryReference.variables.cloudId;

	return (
		<HubErrorBoundary
			packageName="hub"
			id="hub-page"
			fallback={<GenericErrorPage />}
			shouldForceError={true}
			operationNames={['hubBootstrapWrapperQuery']}
		>
			<ContextualAnalyticsData sourceType={SCREEN} sourceName="hub">
				<Suspense
					fallback={
						<UFOLoadHold name="hub-loader">
							<div css={styles.spinnerContainer}>
								<Spinner size="large" label={formatMessage(messages.hubLoadingSpinnerLabel)} />
							</div>
						</UFOLoadHold>
					}
				>
					<HubBootstrapWrapper
						queryReference={hubBootstrapQueryReference}
						cloudId={cloudId}
						{...props}
					>
						{fg('hubs_fix_rerendering_bootstrap_page_media') ? (
							// Flag ON: HubAppShell lives outside Hub so the app
							// shell is not remounted on every page navigation.
							<HubAppShell>
								<Suspense
									fallback={
										<UFOLoadHold name="hub-loader">
											<div css={styles.spinnerContainer}>
												<Spinner
													size="large"
													label={formatMessage(messages.hubLoadingSpinnerLabel)}
												/>
											</div>
										</UFOLoadHold>
									}
								>
									<Hub
										appearance={appearance}
										queryReference={hubPageQueryReference}
										showPageNameAsHubDocumentTitle={props?.showPageNameAsHubDocumentTitle}
									/>
								</Suspense>
							</HubAppShell>
						) : (
							// Flag OFF: original behaviour — Hub renders HubAppShell
							// internally on every page render.
							<Hub
								appearance={appearance}
								queryReference={hubPageQueryReference}
								showPageNameAsHubDocumentTitle={props?.showPageNameAsHubDocumentTitle}
							/>
						)}
					</HubBootstrapWrapper>
				</Suspense>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</HubErrorBoundary>
	);
};
